import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { useSmartNavigate } from '@/lib/nav-prefs/use-smart-navigate';
import {
  Button, Card, CardContent, Input, Label, PageHeader, Checkbox, Badge,
  Tabs, TabsList, TabsTrigger, TabsContent,
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@erp/ui';
import { useBankAccounts, useCreateTransaction } from '@/hooks/use-treasury';
import { ChevronLeft, Loader2, Truck } from '@/components/icons';
import {
  useDeliveryProviderByCode,
  useUpdateDeliveryProvider,
  useDeliveryTransactions,
  useRefreshWalletSummary,
  useDeliveryPickups,
  useDeliveryLogs,
  useMarkTransactionsPaidOut,
  useCreateDeliveryTransaction,
} from '@/hooks/use-delivery-providers';
import { useDataService } from '@/lib/service-context';
import { useQueryClient } from '@tanstack/react-query';
import type { DeliveryProvider } from '@erp/data-access/interfaces';
import {
  STATUS_LABELS,
  IntigoClient,
  fetchCities,
  fetchPickups,
  fetchDistricts,
} from '@erp/delivery';
import { useAuthService } from '@/lib/service-context';
import { AutoConfigureIntigoDialog } from './AutoConfigureIntigoDialog';

function formatDt(n: number): string {
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) + ' DT';
}

function formatDate(s: string | null | undefined): string {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return s;
  }
}

export function DeliveryProviderProfilePage() {
  const { t } = useTranslation();
  const { code } = useParams<{ code: string }>();
  const navigate = useSmartNavigate();
  const { data: provider, isLoading } = useDeliveryProviderByCode(code);

  if (isLoading) {
    return <div className="p-4 text-sm text-muted-foreground">{t('common.loading', 'Chargement…')}</div>;
  }
  if (!provider) {
    return (
      <div className="p-6 space-y-3">
        <p className="text-sm text-muted-foreground">
          {t('settings.deliveryProviders.notFound', 'Transporteur introuvable.')}
        </p>
        <Button variant="outline" onClick={() => navigate('/settings/delivery-providers')}>
          {t('common.back', 'Retour')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Button asChild variant="ghost" size="sm">
          <Link to="/settings/delivery-providers">
            <ChevronLeft size={16} className="rtl:-scale-x-100" />
            {t('common.back', 'Retour')}
          </Link>
        </Button>
      </div>
      <PageHeader
        title={
          <span className="flex items-center gap-2">
            <Truck size={20} className="text-primary" />
            {provider.displayName}
            {provider.isDefault && (
              <Badge variant="secondary">
                {t('settings.deliveryProviders.defaultBadge', 'Par défaut')}
              </Badge>
            )}
            <WebhookStatusBadge providerId={provider.id} />
          </span>
        }
        subtitle={t('settings.deliveryProviders.profileSubtitle', 'Configuration, wallet et journal des appels API.')}
      />

      <Tabs defaultValue="config" className="space-y-4">
        <TabsList>
          <TabsTrigger value="config">
            {t('settings.deliveryProviders.tabs.config', 'Configuration')}
          </TabsTrigger>
          <TabsTrigger value="wallet">
            {t('settings.deliveryProviders.tabs.wallet', 'Wallet')}
          </TabsTrigger>
          <TabsTrigger value="pickups">
            {t('settings.deliveryProviders.tabs.pickups', 'Pickups')}
          </TabsTrigger>
          <TabsTrigger value="logs">
            {t('settings.deliveryProviders.tabs.logs', 'Logs')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="config">
          <ConfigTab provider={provider} />
        </TabsContent>
        <TabsContent value="wallet">
          <WalletTab provider={provider} />
        </TabsContent>
        <TabsContent value="pickups">
          <PickupsTab provider={provider} />
        </TabsContent>
        <TabsContent value="logs">
          <LogsTab provider={provider} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============================================================
// Config tab
// ============================================================

function ConfigTab({ provider }: { provider: DeliveryProvider }) {
  const { t } = useTranslation();
  const update = useUpdateDeliveryProvider(provider.id);
  const ds = useDataService();
  const authService = useAuthService();
  const qc = useQueryClient();

  const [isEnabled, setIsEnabled] = useState(provider.isEnabled);
  const [displayName, setDisplayName] = useState(provider.displayName);
  const [apiKey, setApiKey] = useState(provider.apiKey ?? '');
  const [webhookSecret, setWebhookSecret] = useState(provider.webhookSecret ?? '');
  const [businessId, setBusinessId] = useState(provider.businessId ?? '');
  const [defaultPickupIndex, setDefaultPickupIndex] = useState<string>(
    provider.defaultPickupIndex == null ? '' : String(provider.defaultPickupIndex),
  );
  // Frais de livraison sont maintenant globaux (store_settings.delivery_fee).
  // On garde la valeur DB en lecture seule pour ne pas perdre l'info,
  // mais l'UI ne la modifie plus.
  const defaultFee = String(provider.defaultFee);
  const [defaultPackageSize, setDefaultPackageSize] = useState(String(provider.defaultPackageSize));

  // Test connection state
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; msg: string } | null>(null);

  // Auto-sync state (after Save)
  const [autoSyncing, setAutoSyncing] = useState(false);
  const [syncReport, setSyncReport] = useState<string | null>(null);

  // Auto-configure Intigo webhook dialog
  const [autoCfgOpen, setAutoCfgOpen] = useState(false);
  const [autoCfgDone, setAutoCfgDone] = useState(false);

  useEffect(() => {
    setIsEnabled(provider.isEnabled);
    setDisplayName(provider.displayName);
    setApiKey(provider.apiKey ?? '');
    setWebhookSecret(provider.webhookSecret ?? '');
    setBusinessId(provider.businessId ?? '');
    setDefaultPickupIndex(provider.defaultPickupIndex == null ? '' : String(provider.defaultPickupIndex));
    setDefaultPackageSize(String(provider.defaultPackageSize));
  }, [provider]);

  // Build the absolute webhook URL the user must paste in Intigo's dashboard.
  const webhookUrl = useMemo(() => {
    if (typeof window === 'undefined') return `/api/delivery/${provider.code}/webhook`;
    return `${window.location.origin}/api/delivery/${provider.code}/webhook`;
  }, [provider.code]);

  function generateSecret() {
    // 32 bytes hex — 256-bit entropy, plenty for HMAC
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    const hex = Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
    setWebhookSecret(hex);
  }

  async function copyToClipboard(value: string) {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // ignore — old browsers
    }
  }

  /** After Save with a valid api_key + isEnabled, pull cities/districts/pickups
   *  in the background so the user doesn't have to click "Synchroniser" manually. */
  async function autoSync() {
    if (provider.code !== 'intigo') return;
    setAutoSyncing(true);
    setSyncReport(null);
    try {
      const client = new IntigoClient({
        proxyBaseUrl: '/api/delivery/intigo',
        getAccessToken: () => authService.getAccessToken(),
        supabaseUrl: import.meta.env.VITE_SUPABASE_URL,
        supabaseAnonKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      });
      const pickupsRemote = await fetchPickups(client);
      await ds.deliveryProviders.replacePickups({
        providerId: provider.id,
        pickups: pickupsRemote.map(p => ({
          pickupIndex: p.index,
          streetAddress: p.street_address,
          cityName: p.city_name,
          districtName: p.district_name,
          hasGps: p.has_gps,
        })),
      });
      const citiesRemote = await fetchCities(client);
      await ds.deliveryProviders.replaceCities({
        providerId: provider.id,
        cities: citiesRemote.map(c => ({ externalId: c.id, name: c.name, nameAr: c.name_ar ?? null })),
      });
      let totalDistricts = 0;
      for (const city of citiesRemote) {
        const districts = await fetchDistricts(client, city.id);
        if (!districts.length) continue;
        await ds.deliveryProviders.replaceDistricts({
          providerId: provider.id,
          cityExternalId: city.id,
          districts: districts.map(d => ({ externalId: d.id, name: d.name })),
        });
        totalDistricts += districts.length;
      }
      qc.invalidateQueries({ queryKey: ['delivery-providers'] });
      setSyncReport(
        t(
          'settings.deliveryProviders.config.syncedAfterSave',
          `Sync auto : ${pickupsRemote.length} pickup(s), ${citiesRemote.length} ville(s), ${totalDistricts} délégation(s).`,
        ) as string,
      );
    } catch (e) {
      setSyncReport(t('settings.deliveryProviders.config.syncFailed', 'Sync auto échouée — utilisez le bouton "Synchroniser" dans l\'onglet Pickups.') as string);
      // eslint-disable-next-line no-console
      console.warn('autoSync failed:', e);
    } finally {
      setAutoSyncing(false);
    }
  }

  async function handleSave() {
    const cleanedApiKey = apiKey.trim() || null;
    const wasMissingKey = !provider.apiKey;
    await update.mutateAsync({
      isEnabled,
      displayName: displayName.trim(),
      apiKey: cleanedApiKey,
      webhookSecret: webhookSecret.trim() || null,
      businessId: businessId.trim() || null,
      defaultPickupIndex: defaultPickupIndex === '' ? null : Number(defaultPickupIndex),
      defaultFee: Number(defaultFee || 0),
      defaultPackageSize: Number(defaultPackageSize || 1),
    });
    // If they just configured the key for the first time and the provider is enabled,
    // run the references sync without asking.
    if (cleanedApiKey && isEnabled && wasMissingKey) {
      // fire and forget; UI shows "Sync auto en cours..." inline
      void autoSync();
    }
  }

  /** Test the API key against Intigo. We save first (so the proxy can read the
   *  freshly-typed key), then call /v3/pickup-addresses/. */
  async function handleTest() {
    setTestResult(null);
    setTesting(true);
    try {
      // Save first so the proxy reads the correct key
      await update.mutateAsync({ apiKey: apiKey.trim() || null });
      const client = new IntigoClient({
        proxyBaseUrl: '/api/delivery/intigo',
        getAccessToken: () => authService.getAccessToken(),
        supabaseUrl: import.meta.env.VITE_SUPABASE_URL,
        supabaseAnonKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      });
      const pickups = await fetchPickups(client);
      setTestResult({
        ok: true,
        msg: t('settings.deliveryProviders.config.testOk', `Connexion OK. ${pickups.length} adresse(s) pickup trouvée(s).`) as string,
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setTestResult({ ok: false, msg });
    } finally {
      setTesting(false);
    }
  }

  return (
    <Card>
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-sm font-medium">
              {t('settings.deliveryProviders.config.enabled', 'Transporteur activé')}
            </Label>
            <p className="text-xs text-muted-foreground">
              {t(
                'settings.deliveryProviders.config.enabledHint',
                "Désactivez pour mettre en pause les envois sans perdre la configuration.",
              )}
            </p>
          </div>
          <Checkbox
            checked={isEnabled}
            onCheckedChange={(v) => setIsEnabled(v === true)}
            aria-label="Enabled"
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="cfg-name">
              {t('settings.deliveryProviders.config.displayName', "Nom d'affichage")}
            </Label>
            <Input id="cfg-name" value={displayName} onChange={e => setDisplayName(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cfg-bizid">
              {t('settings.deliveryProviders.config.businessId', 'Business ID')}
            </Label>
            <Input id="cfg-bizid" value={businessId} onChange={e => setBusinessId(e.target.value)} placeholder="—" />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="cfg-apikey">
              {t('settings.deliveryProviders.config.apiKey', 'Clé API')}
            </Label>
            <div className="flex gap-2">
              <Input
                id="cfg-apikey"
                value={apiKey}
                onChange={e => setApiKey(e.target.value)}
                placeholder="X-API-Key"
                type="password"
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleTest}
                disabled={!apiKey.trim() || testing}
              >
                {testing
                  ? t('common.testing', 'Test…')
                  : t('settings.deliveryProviders.config.testConnection', 'Tester')}
              </Button>
            </div>
            {testResult && (
              <div
                className={`rounded-md border p-2 text-xs ${
                  testResult.ok
                    ? 'border-emerald-500/40 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400'
                    : 'border-destructive/40 bg-destructive/5 text-destructive'
                }`}
              >
                {testResult.msg}
              </div>
            )}
            <p className="text-[11px] text-muted-foreground">
              {t(
                'settings.deliveryProviders.config.apiKeyHint',
                "Stockée dans la base du tenant. Cliquez « Tester » pour vérifier la clé sans tout sauvegarder.",
              )}
            </p>
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="cfg-webhook">
              {t('settings.deliveryProviders.config.webhookSecret', 'Secret webhook (HMAC)')}
            </Label>
            <div className="flex gap-2">
              <Input
                id="cfg-webhook"
                value={webhookSecret}
                onChange={e => setWebhookSecret(e.target.value)}
                type="password"
                placeholder={t('settings.deliveryProviders.config.webhookSecretPlaceholder', 'Cliquez « Générer » pour un secret aléatoire') as string}
              />
              <Button type="button" variant="outline" onClick={generateSecret}>
                {t('settings.deliveryProviders.config.generate', 'Générer')}
              </Button>
            </div>
            <div className="flex items-center gap-2 rounded-md border bg-muted/30 p-2 text-[11px]">
              <span className="text-muted-foreground shrink-0">URL :</span>
              <code className="font-mono break-all flex-1">{webhookUrl}</code>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => copyToClipboard(webhookUrl)}
              >
                {t('common.copy', 'Copier')}
              </Button>
            </div>
            {provider.code === 'intigo' && (
              <div className="flex items-center justify-between gap-2 rounded-md border border-primary/30 bg-primary/5 p-2">
                <div className="text-[11px]">
                  <p className="font-medium">
                    {t('settings.deliveryProviders.config.autoCfgTitle', 'Configurer chez Intigo en un clic')}
                  </p>
                  <p className="text-muted-foreground">
                    {t(
                      'settings.deliveryProviders.config.autoCfgHint',
                      "Évite la copie manuelle. Demande votre login Intigo une seule fois (jamais stocké).",
                    )}
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setAutoCfgOpen(true)}
                  disabled={!webhookSecret.trim()}
                >
                  {autoCfgDone
                    ? t('settings.deliveryProviders.config.autoCfgDone', '✓ Configuré')
                    : t('settings.deliveryProviders.config.autoCfgBtn', 'Auto-configurer')}
                </Button>
              </div>
            )}
            <p className="text-[11px] text-muted-foreground">
              {t(
                'settings.deliveryProviders.config.webhookHelp',
                "Sinon, allez sur Intigo : Paramètres → Webhook, et collez l'URL et le secret manuellement.",
              )}
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="cfg-pickup">
              {t('settings.deliveryProviders.config.defaultPickup', 'Pickup par défaut (index)')}
            </Label>
            <Input
              id="cfg-pickup"
              type="number"
              min={0}
              value={defaultPickupIndex}
              onChange={e => setDefaultPickupIndex(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cfg-size">
              {t('settings.deliveryProviders.config.defaultPackageSize', 'Taille colis par défaut')}
            </Label>
            <Input
              id="cfg-size"
              type="number"
              min={1}
              max={7}
              value={defaultPackageSize}
              onChange={e => setDefaultPackageSize(e.target.value)}
            />
            <p className="text-[11px] text-muted-foreground">1-3 = Petit, 4 = Grand, 5-7 = XL</p>
          </div>
          {/* Le frais facturé au client final est maintenant un seul réglage
              global dans Boutique → Paramètres → Frais de livraison.
              On garde le champ default_fee en DB pour compat future
              (si on veut un jour facturer des frais différents par
              transporteur), mais il n'est plus exposé dans l'UI. */}
        </div>

        {(autoSyncing || syncReport) && (
          <div className={`rounded-md border p-2 text-xs ${
            autoSyncing
              ? 'border-primary/40 bg-primary/5 text-primary'
              : 'border-emerald-500/40 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400'
          }`}>
            {autoSyncing
              ? t('settings.deliveryProviders.config.autoSyncing', 'Synchronisation des villes et pickups en cours…')
              : syncReport}
          </div>
        )}

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={update.isPending || autoSyncing}>
            {t('common.save', 'Enregistrer')}
          </Button>
        </div>
      </CardContent>

      <AutoConfigureIntigoDialog
        open={autoCfgOpen}
        onClose={() => setAutoCfgOpen(false)}
        webhookUrl={webhookUrl}
        webhookSecret={webhookSecret}
        onSuccess={() => setAutoCfgDone(true)}
      />
    </Card>
  );
}

// ============================================================
// Wallet tab
// ============================================================

function WalletTab({ provider }: { provider: DeliveryProvider }) {
  const { t } = useTranslation();
  const refresh = useRefreshWalletSummary(provider.id);
  const markPaid = useMarkTransactionsPaidOut();
  const createTx = useCreateDeliveryTransaction();
  const createBankTx = useCreateTransaction();
  const { data: txs } = useDeliveryTransactions({ providerId: provider.id });
  const { data: bankAccounts } = useBankAccounts();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [payoutBankId, setPayoutBankId] = useState<string>('');
  const [payoutDate, setPayoutDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [payoutRef, setPayoutRef] = useState<string>('');
  const [payoutError, setPayoutError] = useState<string | null>(null);

  const available = (txs ?? []).filter(t => t.status === 'available');
  const selectedTotal = (txs ?? [])
    .filter(t => selected.has(t.id))
    .reduce((sum, t) => sum + (t.kind === 'fee' || t.kind === 'adjustment' ? -t.amount : t.amount), 0);

  function toggle(id: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function handleConfirmPayout() {
    if (!payoutBankId || selected.size === 0 || selectedTotal <= 0) {
      setPayoutError(t('settings.deliveryProviders.wallet.payoutMissing', 'Compte et transactions requis.') as string);
      return;
    }
    setPayoutError(null);
    try {
      // 1. Create the bank transaction (incoming transfer from carrier)
      await createBankTx.mutateAsync({
        bankAccountId: payoutBankId,
        transactionDate: payoutDate,
        valueDate: payoutDate,
        direction: 'in',
        amount: selectedTotal,
        transactionType: 'transfer_in',
        reference: payoutRef || `${provider.displayName} payout`,
        description: `Payout ${provider.displayName} (${selected.size} colis)`,
      });
      // 2. Mark wallet transactions paid_out
      await markPaid.mutateAsync(Array.from(selected));
      setSelected(new Set());
      setPayoutOpen(false);
    } catch (e) {
      setPayoutError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground">
              {t('settings.deliveryProviders.wallet.available', 'Disponible')}
            </div>
            <div className="mt-1 text-2xl font-semibold text-emerald-600">
              {formatDt(provider.cachedBalanceAvailable)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-muted-foreground">
              {t('settings.deliveryProviders.wallet.pending', 'En attente')}
            </div>
            <div className="mt-1 text-2xl font-semibold text-amber-600">
              {formatDt(provider.cachedBalancePending)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex flex-col gap-2">
            <div className="text-xs text-muted-foreground">
              {t('settings.deliveryProviders.wallet.lastSync', 'Dernier rafraîchissement')}
            </div>
            <div className="text-sm">{formatDate(provider.cachedBalanceAt)}</div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => refresh.mutate()}
              disabled={refresh.isPending}
            >
              <Loader2 size={14} className={refresh.isPending ? 'animate-spin' : ''} />
              {t('common.refresh', 'Rafraîchir')}
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <h2 className="text-sm font-medium">
                {t('settings.deliveryProviders.wallet.transactionsTitle', 'Transactions')}
              </h2>
              <p className="text-xs text-muted-foreground">
                {t(
                  'settings.deliveryProviders.wallet.transactionsHint',
                  'Sélectionnez des transactions disponibles puis marquez-les comme versées.',
                )}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {selected.size > 0 && (
                <span className="text-xs text-muted-foreground">
                  {t('settings.deliveryProviders.wallet.selectedTotal', 'Total sélectionné')}:{' '}
                  <span className="font-medium">{formatDt(selectedTotal)}</span>
                </span>
              )}
              <Button
                size="sm"
                onClick={() => {
                  setPayoutRef(`${provider.displayName} payout ${new Date().toLocaleDateString('fr-FR')}`);
                  setPayoutOpen(true);
                }}
                disabled={selected.size === 0 || selectedTotal <= 0 || markPaid.isPending}
              >
                {t('settings.deliveryProviders.wallet.markPaidOut', 'Marquer versé')}
              </Button>
              {/* Dev helper to seed a fake transaction; visible only when the
                  provider code is 'mock'. */}
              {provider.code === 'mock' && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    createTx.mutate({
                      providerId: provider.id,
                      kind: 'cod_collected',
                      amount: 45.5,
                      status: 'pending',
                      occurredAt: new Date().toISOString(),
                    });
                  }}
                  disabled={createTx.isPending}
                >
                  + Mock TX
                </Button>
              )}
            </div>
          </div>

          {!txs || txs.length === 0 ? (
            <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
              {t('settings.deliveryProviders.wallet.empty', 'Aucune transaction.')}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[40px]" />
                  <TableHead>{t('settings.deliveryProviders.wallet.col.date', 'Date')}</TableHead>
                  <TableHead>{t('settings.deliveryProviders.wallet.col.kind', 'Type')}</TableHead>
                  <TableHead>{t('settings.deliveryProviders.wallet.col.ref', 'Réf.')}</TableHead>
                  <TableHead>{t('settings.deliveryProviders.wallet.col.status', 'Statut')}</TableHead>
                  <TableHead className="text-right">{t('settings.deliveryProviders.wallet.col.amount', 'Montant')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {txs.map(tx => {
                  const isAvailable = tx.status === 'available';
                  return (
                    <TableRow key={tx.id}>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selected.has(tx.id)}
                          onChange={() => toggle(tx.id)}
                          disabled={!isAvailable}
                          aria-label="select"
                        />
                      </TableCell>
                      <TableCell className="text-xs whitespace-nowrap">
                        {formatDate(tx.occurredAt ?? tx.createdAt)}
                      </TableCell>
                      <TableCell className="text-xs">{tx.kind}</TableCell>
                      <TableCell className="text-xs font-mono">{tx.externalRef ?? '—'}</TableCell>
                      <TableCell>
                        <StatusBadge status={tx.status} />
                      </TableCell>
                      <TableCell className="text-right font-mono">{formatDt(tx.amount)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}

          <p className="text-[11px] text-muted-foreground">
            {t(
              'settings.deliveryProviders.wallet.availableNote',
              `${available.length} transaction(s) disponible(s). Marquer "versé" crée une écriture trésorerie sur le compte choisi.`,
            )}
          </p>
        </CardContent>
      </Card>

      <Dialog open={payoutOpen} onOpenChange={(o) => { if (!o) setPayoutOpen(false); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('settings.deliveryProviders.wallet.payoutTitle', 'Recevoir le versement')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {t(
                'settings.deliveryProviders.wallet.payoutHint',
                `Confirmez la réception de ${formatDt(selectedTotal)} sur un compte bancaire. Une transaction trésorerie sera créée et les ${selected.size} encaissements passeront en "versé".`,
              )}
            </p>
            <div className="space-y-1.5">
              <Label>{t('settings.deliveryProviders.wallet.bankAccount', 'Compte bancaire')}</Label>
              <Select value={payoutBankId} onValueChange={setPayoutBankId}>
                <SelectTrigger>
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  {(bankAccounts ?? []).filter(a => a.isActive).map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.name} — {a.bankName ?? ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{t('settings.deliveryProviders.wallet.payoutDate', 'Date du versement')}</Label>
                <Input type="date" value={payoutDate} onChange={e => setPayoutDate(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>{t('settings.deliveryProviders.wallet.payoutAmount', 'Montant')}</Label>
                <Input value={formatDt(selectedTotal)} disabled />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t('settings.deliveryProviders.wallet.payoutRef', 'Référence')}</Label>
              <Input value={payoutRef} onChange={e => setPayoutRef(e.target.value)} />
            </div>
            {payoutError && (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">{payoutError}</div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayoutOpen(false)}>
              {t('common.cancel', 'Annuler')}
            </Button>
            <Button onClick={handleConfirmPayout} disabled={!payoutBankId || createBankTx.isPending || markPaid.isPending}>
              {t('common.confirm', 'Confirmer')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function WebhookStatusBadge({ providerId }: { providerId: string }) {
  const { t } = useTranslation();
  const { data: logs } = useDeliveryLogs({ providerId, limit: 1 });
  // The webhook receiver inserts inbound logs — if any exists, Intigo is
  // successfully POSTing to us, so the webhook is wired correctly.
  const hasInbound = (logs ?? []).some(l => l.direction === 'inbound');
  if (hasInbound) {
    return (
      <Badge variant="default" className="text-[10px]">
        ✓ {t('settings.deliveryProviders.webhookOk', 'Webhook OK')}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-[10px] border-amber-500/40 text-amber-700 dark:text-amber-400">
      ⚠ {t('settings.deliveryProviders.webhookPending', 'Webhook en attente')}
    </Badge>
  );
}

function StatusBadge({ status }: { status: 'available' | 'pending' | 'paid_out' }) {
  if (status === 'available') return <Badge variant="default">disponible</Badge>;
  if (status === 'pending') return <Badge variant="outline">en attente</Badge>;
  return <Badge variant="secondary">versé</Badge>;
}

// ============================================================
// Pickups tab
// ============================================================

function PickupsTab({ provider }: { provider: DeliveryProvider }) {
  const { t } = useTranslation();
  const { data: pickups, isLoading } = useDeliveryPickups(provider.id);
  const ds = useDataService();
  const authService = useAuthService();
  const qc = useQueryClient();
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [syncReport, setSyncReport] = useState<string | null>(null);

  async function handleSync() {
    if (provider.code !== 'intigo') {
      setSyncError(`Sync not supported for provider "${provider.code}"`);
      return;
    }
    setSyncing(true);
    setSyncError(null);
    setSyncReport(null);
    try {
      const client = new IntigoClient({
        proxyBaseUrl: '/api/delivery/intigo',
        getAccessToken: () => authService.getAccessToken(),
        supabaseUrl: import.meta.env.VITE_SUPABASE_URL,
        supabaseAnonKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      });
      // 1. Pickups
      const pickupsRemote = await fetchPickups(client);
      await ds.deliveryProviders.replacePickups({
        providerId: provider.id,
        pickups: pickupsRemote.map(p => ({
          pickupIndex: p.index,
          streetAddress: p.street_address,
          cityName: p.city_name,
          districtName: p.district_name,
          hasGps: p.has_gps,
        })),
      });
      // 2. Cities
      const citiesRemote = await fetchCities(client);
      await ds.deliveryProviders.replaceCities({
        providerId: provider.id,
        cities: citiesRemote.map(c => ({
          externalId: c.id,
          name: c.name,
          nameAr: c.name_ar ?? null,
        })),
      });
      // 3. Districts for every city (sequential to stay friendly)
      let totalDistricts = 0;
      for (const city of citiesRemote) {
        const districts = await fetchDistricts(client, city.id);
        if (districts.length === 0) continue;
        await ds.deliveryProviders.replaceDistricts({
          providerId: provider.id,
          cityExternalId: city.id,
          districts: districts.map(d => ({ externalId: d.id, name: d.name })),
        });
        totalDistricts += districts.length;
      }
      setSyncReport(
        t(
          'settings.deliveryProviders.pickups.synced',
          `Synchronisé : ${pickupsRemote.length} pickup(s), ${citiesRemote.length} ville(s), ${totalDistricts} délégation(s).`,
        ) as string,
      );
      qc.invalidateQueries({ queryKey: ['delivery-providers'] });
    } catch (e) {
      setSyncError(e instanceof Error ? e.message : String(e));
    } finally {
      setSyncing(false);
    }
  }

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-medium">
              {t('settings.deliveryProviders.pickups.title', 'Adresses de pickup')}
            </h2>
            <p className="text-xs text-muted-foreground">
              {t(
                'settings.deliveryProviders.pickups.hint',
                "Synchronisées depuis le compte du transporteur. Le pickup index par défaut est utilisé pour chaque commande.",
              )}
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={handleSync} disabled={syncing || !provider.isEnabled}>
            <Loader2 size={14} className={syncing ? 'animate-spin' : ''} />
            {t('common.sync', 'Synchroniser')}
          </Button>
        </div>
        {syncError && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">{syncError}</div>
        )}
        {syncReport && !syncError && (
          <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-2 text-xs text-emerald-700 dark:text-emerald-400">{syncReport}</div>
        )}

        {isLoading ? (
          <div className="text-sm text-muted-foreground">{t('common.loading', 'Chargement…')}</div>
        ) : !pickups || pickups.length === 0 ? (
          <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
            {t(
              'settings.deliveryProviders.pickups.empty',
              "Aucune adresse pickup synchronisée. Disponible une fois la clé API renseignée et la synchronisation lancée (P2).",
            )}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Index</TableHead>
                <TableHead>{t('settings.deliveryProviders.pickups.col.address', 'Adresse')}</TableHead>
                <TableHead>{t('settings.deliveryProviders.pickups.col.city', 'Ville')}</TableHead>
                <TableHead>GPS</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pickups.map(p => (
                <TableRow key={p.id}>
                  <TableCell className="font-mono">{p.pickupIndex}</TableCell>
                  <TableCell className="text-sm">{p.streetAddress ?? '—'}</TableCell>
                  <TableCell className="text-sm">
                    {[p.cityName, p.districtName].filter(Boolean).join(' / ') || '—'}
                  </TableCell>
                  <TableCell>{p.hasGps ? '✓' : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================
// Logs tab
// ============================================================

function LogsTab({ provider }: { provider: DeliveryProvider }) {
  const { t } = useTranslation();
  const { data: logs, isLoading } = useDeliveryLogs({ providerId: provider.id, limit: 50 });

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <h2 className="text-sm font-medium">
          {t('settings.deliveryProviders.logs.title', 'Journal des appels API')}
        </h2>
        <p className="text-xs text-muted-foreground">
          {t(
            'settings.deliveryProviders.logs.hint',
            "Les 50 derniers appels (sortants vers le transporteur ou webhooks entrants).",
          )}
        </p>

        {isLoading ? (
          <div className="text-sm text-muted-foreground">{t('common.loading', 'Chargement…')}</div>
        ) : !logs || logs.length === 0 ? (
          <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
            {t('settings.deliveryProviders.logs.empty', 'Aucun appel enregistré pour ce transporteur.')}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('settings.deliveryProviders.logs.col.date', 'Date')}</TableHead>
                <TableHead>{t('settings.deliveryProviders.logs.col.direction', 'Sens')}</TableHead>
                <TableHead>{t('settings.deliveryProviders.logs.col.endpoint', 'Endpoint')}</TableHead>
                <TableHead>{t('settings.deliveryProviders.logs.col.status', 'HTTP')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map(l => {
                const code = l.responseStatus ?? 0;
                const known = STATUS_LABELS[code];
                return (
                  <TableRow key={l.id}>
                    <TableCell className="text-xs whitespace-nowrap">{formatDate(l.createdAt)}</TableCell>
                    <TableCell>
                      <Badge variant={l.direction === 'inbound' ? 'secondary' : 'outline'}>{l.direction}</Badge>
                    </TableCell>
                    <TableCell className="text-xs font-mono truncate max-w-[300px]">
                      {(l.method ?? '').toUpperCase()} {l.endpoint ?? '—'}
                    </TableCell>
                    <TableCell className="text-xs font-mono">
                      {code || '—'} {known ? `(${known})` : ''}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
